/**
 * ADS context (growth kit) — the only point where the app composes
 * provider + capping + rewards.
 *
 * Decision chain, outside in:
 *   1. `resolveAdsConfig(env, overrides)` — flags **OFF by default**
 *      (`VITE_ADS_ENABLED`), with back-office overrides on top.
 *   2. `shouldShowAds(config, tier)` from the monetization kit — **premium ⇒ 0
 *      ads immediately** (optimistic local flag: no wait for sync).
 *   3. `makeAdProvider(...)` — with the preceding result `false`, returns the
 *      no-op and **loads no SDK** (zero network requests).
 *   4. `adGate(...)` — decides whether it is TIME to show now (capping + breakpoint).
 *
 * Guest-first: capping and rewards live in the device's `localStorage`,
 * so they work **without an account and offline**.
 */
import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  type ParentComponent,
  useContext,
} from "solid-js";
import {
  adCapPolicyFromSettings,
  AdCapsStore,
  adGate,
  type AdGateDecision,
  type AdKind,
  type AdProvider,
  type AdReward,
  AdRewardsStore,
  adsAreLive,
  type AdsConfig,
  billingQueryKeys,
  type components,
  HABITS_AD_PLACEMENTS,
  type HabitsPlacementId,
  makeAdProvider,
  NoneAdProvider,
  placementDef,
  resolveAdsConfig,
  resolveMonetizationConfig,
  shouldShowAds,
} from "@habits/shared";
import { usePublicConfig } from "@/features/app-config/use-public-config";
import { api, createApiQuery } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { adsEnv } from "./ads-env";

type ErrorModel = components["schemas"]["ErrorModel"];
type BillingConfig = components["schemas"]["BillingConfigResponse"];
type BillingUsage = components["schemas"]["BillingUsageResponse"];

/** Session signals that feed the gate (the app-shell moves them itself). */
interface SessionSignals {
  activeMs: number;
  unitsCompleted: number;
  isFirstEver: boolean;
}

export interface AdsContextValue {
  config: Accessor<AdsConfig>;
  /** Are ads live? (flags + tier + network). `false` ⇒ nothing mounts. */
  live: Accessor<boolean>;
  provider: Accessor<AdProvider>;
  /** Should this placement be shown NOW? */
  gate: (placementId: HabitsPlacementId, inFlight?: boolean) => AdGateDecision;
  /** Marks a value unit as completed (list done, daily plan…). */
  completeValueUnit: () => void;
  /** Declares that a task is in progress (active input, timer, pending tx). */
  setInFlight: (value: boolean) => void;
  /** Runs an interstitial. Returns `true` if it was actually shown. */
  showInterstitial: (placementId: HabitsPlacementId) => Promise<boolean>;
  /** Runs a rewarded ad. Returns the GRANTED reward, or `null`. */
  showRewarded: (placementId: HabitsPlacementId) => Promise<AdReward | null>;
  /** Records that a rewarded offer was SHOWN (for backoff). */
  recordOffer: (placementId: HabitsPlacementId) => void;
  /** Screen counters (native/banner) — do not persist. */
  setScreenCounts: (counts: { natives?: number; banners?: number }) => void;
  rewards: AdRewardsStore;
}

const AdsContext = createContext<AdsContextValue>();

const FIRST_SESSION_KEY = "ads.session.seen";

/** Safe storage: in SSR/tests without a DOM, falls back to an in-memory map. */
function safeStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function memoryStorage() {
  const map = new Map<string, string>();
  return {
    getItem: (k: string) => map.get(k) ?? null,
    setItem: (k: string, v: string) => {
      map.set(k, v);
    },
    removeItem: (k: string) => {
      map.delete(k);
    },
  };
}

export const AdsProvider: ParentComponent = (props) => {
  const session = useSession();
  const storage = safeStorage() ?? memoryStorage();

  // CONFIG_OVER_CODE: capping limits (`adcaps.*`) are resolved by the
  // server and the admin adjusts them ON THE FLY. Without response, `{}` ⇒ uses
  // `DEFAULT_AD_CAP_POLICY` (code default, mirroring Rust).
  const publicConfig = usePublicConfig();
  const capPolicy = createMemo(() => adCapPolicyFromSettings(publicConfig()?.adcaps));

  // This provider lives in the ENTIRE app, so the query is deliberately
  // cheap: long cache and ZERO retries. If no response (e.g. a
  // guest, for whom endpoint requires session) layer falls back to env
  // config, which defaults to OFF — never retries in a loop against
  // server rate-limiter for an ad.
  // CAREFUL (measured bug, P23): there was also a `retry: false` here. `staleTime` is
  // per OBSERVER, but `retry` belongs to the QUERY: by sharing the cache key
  // (`billingQueryKeys.config()`) with `PlanOverview` and the profile
  // landing page, this override reconfigured the other consumers at a DISTANCE and
  // left `/profile/plan` with ZERO retries → a transient 500 rendered the entire
  // error box instead of retrying (measured: 1 request and an error after
  // ~800 ms). What makes this query cheap is the 5-minute `staleTime`, not
  // removing retries: the QueryClient default is kept (retry: 2) so
  // that ALL consumers of the key behave the same.
  const configQuery = createApiQuery<BillingConfig, ErrorModel>(
    () => billingQueryKeys.config(),
    async () => api.GET("/api/billing/config", {}),
    () => ({ staleTime: 300_000 }),
  );

  const usageQuery = createApiQuery<BillingUsage, ErrorModel>(
    () => billingQueryKeys.usage(),
    async () => api.GET("/api/billing/usage", {}),
    () => ({
      staleTime: 30_000,
      enabled: !session.isVisitor() && configQuery.data?.enabled === true,
    }),
  );

  // Bundle env + overrides served by the back-office (env → override).
  const adsConfig = createMemo(() =>
    resolveAdsConfig(
      adsEnv(),
      Object.entries(configQuery.data?.ads ?? {}).map(([key, value]) => ({ key, value })),
    ),
  );

  /**
   * Monetization kit verdict. The visitor inherits `defaultTier`
   * (free ⇒ ads on), and premium disables ads immediately.
   */
  const monetizationSaysAds = createMemo(() => {
    const remote = configQuery.data;
    if (!remote) {
      // Guest-first: a visitor CANNOT be premium (there is no subscription
      // without an account), so it inherits the default tier — free, ad-supported.
      // For a signed-in user, however, we wait for the actual verdict: an
      // ad flash to someone paying is the worst of both worlds.
      return session.isVisitor();
    }
    const config = resolveMonetizationConfig({}, []);
    config.enabled = remote.enabled;
    config.defaultTier = remote.defaultTier;
    config.tiers = remote.tiers.map((t) => ({
      id: t.id,
      label: t.label,
      adsEnabled: t.adsEnabled,
      limits: {
        storage_bytes: t.limits.storageBytes ?? null,
        items: t.limits.maxItems ?? null,
        collections: t.limits.maxCollections ?? null,
        orgs: t.limits.maxOrgs ?? null,
        attachments: t.limits.maxAttachments ?? null,
      },
    }));
    return shouldShowAds(config, usageQuery.data?.tier);
  });

  const live = createMemo(() => adsAreLive(adsConfig(), monetizationSaysAds()));

  // Always START with the no-op: until ads are confirmed
  // live, no request is even possible.
  const [provider, setProvider] = createSignal<AdProvider>(new NoneAdProvider());

  const caps = new AdCapsStore(storage, session.userId() || "guest");
  const rewards = new AdRewardsStore(storage, session.userId() || "guest");

  const startedAt = Date.now();
  const [unitsCompleted, setUnitsCompleted] = createSignal(0);
  const [inFlight, setInFlight] = createSignal(false);

  const isFirstEver = (() => {
    const seen = storage.getItem(FIRST_SESSION_KEY) !== null;
    storage.setItem(FIRST_SESSION_KEY, "1");
    return !seen;
  })();

  const sessionSignals = (): SessionSignals => ({
    activeMs: Date.now() - startedAt,
    unitsCompleted: unitsCompleted(),
    isFirstEver,
  });

  // The SDK loads only when ads are LIVE: while `live()` is
  // false, there is no real provider, so no request is possible.
  createEffect(() => {
    if (!live()) {
      setProvider(() => new NoneAdProvider());
      // After a premium purchase: 0 ads immediately ⇒ local capping is forgotten.
      caps.reset();
      return;
    }
    const next = makeAdProvider(adsConfig());
    const config = adsConfig();
    void (async () => {
      await next.init({
        childDirected: config.childDirected,
        maxAdContentRating: config.maxAdContentRating,
      });
      setProvider(() => next);
    })();
  });

  const gate = (placementId: HabitsPlacementId, taskInFlight?: boolean): AdGateDecision => {
    const def = placementDef(HABITS_AD_PLACEMENTS, placementId);
    if (!def || !live()) {
      // An undeclared placement does not exist: nothing is rendered.
      return { allow: false, reason: "premium" };
    }
    return adGate({
      kind: def.kind,
      placementId,
      breakpoint: def.breakpoint,
      rewardType: def.reward?.type,
      now: Date.now(),
      caps: caps.snapshot(),
      session: sessionSignals(),
      user: { premium: !monetizationSaysAds(), child: adsConfig().childDirected },
      inFlight: taskInFlight ?? inFlight(),
      policy: capPolicy(),
    });
  };

  const showKind = async (
    placementId: HabitsPlacementId,
    kind: AdKind,
  ): Promise<{ shown: boolean; reward?: AdReward }> => {
    if (!gate(placementId).allow) {
      return { shown: false };
    }
    const p = provider();
    if (!(await p.prepare(kind, placementId))) {
      // No fill: the UI collapses the gap. Never a spinner waiting for an ad.
      return { shown: false };
    }
    return p.show(kind, placementId);
  };

  const value: AdsContextValue = {
    config: adsConfig,
    live,
    provider,
    gate,
    completeValueUnit: () => setUnitsCompleted((n) => n + 1),
    setInFlight,
    rewards,
    recordOffer: (placementId) => caps.recordOffer(placementId),
    setScreenCounts: (counts) => caps.setScreenCounts(counts),
    showInterstitial: async (placementId) => {
      const result = await showKind(placementId, "interstitial");
      if (result.shown) {
        caps.recordInterstitial();
      }
      return result.shown;
    },
    showRewarded: async (placementId) => {
      const def = placementDef(HABITS_AD_PLACEMENTS, placementId);
      const result = await showKind(placementId, "rewarded");
      // The provider's `reward` only CERTIFIES that the user completed the
      // ad; watching halfway earns nothing. WHAT is granted ALWAYS comes from
      // the placement registry, never from the network payload (which is
      // external input). If the reward requires the backend, the server verifies it again.
      const granted = result.reward ? rewards.grant(def?.reward) : null;
      if (granted) {
        caps.recordReward({ type: granted.type, amount: granted.amount }, placementId);
        return { type: granted.type, amount: granted.amount };
      }
      return null;
    },
  };

  return <AdsContext.Provider value={value}>{props.children}</AdsContext.Provider>;
};

/**
 * Access to the ads context. Returns `undefined` if no provider is mounted
 * — intentionally: an app without ads (tiers C/D) must not fail for not mounting it.
 */
export function useAds(): AdsContextValue | undefined {
  return useContext(AdsContext);
}
